import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../layouts/PageTitleContext';
import useBreadcrumbSetup from '../components/useBreadcrumbSetup';
import EntityTable from '../components/Listing/EntityTable';
import Modal from '../components/Modal/Modal';
import { Document, Page, pdfjs } from 'react-pdf';
import usePaymentColumns from "../hooks/paymentColumns";
import { formatAmount } from "../components/Order/paymentColumns";
import PaymentMethodBadge from "../components/Order/PaymentMethodBadge";
import PatientImage from "../components/Patient/PatientImage";
import { formatRelativeDate, isToday } from "../utils/FormatDate";
import { NavLink } from "react-router-dom";
import { API_BASE_URL } from "../api";

import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

export default function Payments() {
  const { t } = useTranslation();
  const { setTitle, setTitleIcon } = usePageTitle();
  const ref = useRef(null);

  const [pdfData,       setPdfData]       = useState(null);
  const [showPdfModal,  setShowPdfModal]  = useState(false);
  const [numPages,      setNumPages]      = useState(null);
  const [pageNumber,    setPageNumber]    = useState(1);

  const columns = usePaymentColumns({
    setPdfData,
    setShowPdfModal,
    setPageNumber,
  });

  // Card-view design for this listing (Table's enableCardView feature, forced on mobile)
  // — reuses the actions column's own dropdown so both views offer identical actions.
  const actionsColumn = columns.find((c) => c.accessor === "actions");
  const renderPaymentCard = (row) => (
    <div className="card h-100 shadow-sm">
      <div className="card-body d-flex flex-column" style={{ padding: 14, gap: 6 }}>
        <NavLink to={`/payments/${row.id}`} style={{ fontSize: 13.5, fontWeight: 600, color: "#111827" }}>
          {row.paymentNumber}
        </NavLink>
        <span style={{ fontSize: 11.5, color: "#9ca3af" }}>{formatRelativeDate(row.paymentCreatedAt)}</span>

        {row.patient ? (
          <div className="d-flex align-items-center gap-2" style={{ marginTop: 2 }}>
            <PatientImage patient={row.patient} size={28} />
            <span style={{ fontSize: 12.5, color: "#374151" }}>
              {row.patient.firstName}{row.patient.lastName ? ` ${row.patient.lastName[0]}.` : ''}
            </span>
          </div>
        ) : (
          <span style={{ fontSize: 11.5, color: "#9ca3af" }}>Visiteur</span>
        )}

        {row.paymentMethod && (
          <div style={{ alignSelf: "flex-start" }}><PaymentMethodBadge method={row.paymentMethod} /></div>
        )}

        <div className="d-flex align-items-center justify-content-between" style={{ marginTop: "auto", paddingTop: 6 }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
            <span style={{ fontSize: 15, fontWeight: 700, color: "#059669" }}>{formatAmount(row.amount)}</span>
            <span style={{ fontSize: 10.5, color: "#9ca3af", fontWeight: 500 }}>dhs</span>
          </div>
          {actionsColumn?.Cell(row)}
        </div>
      </div>
    </div>
  );

  useEffect(() => {
    setTitle(`${t('Payments')}<span style="display:block;font-size:11px;font-weight:400;color:#9ca3af;margin-top:5px;">${t('payments.subtitle')}</span>`);
    document.title = t('Payments');
    setTitleIcon('ti-credit-card');
    return () => setTitleIcon(null);
  }, [setTitle, setTitleIcon, t]);

  useBreadcrumbSetup([
    { label: t('Payments'), url: "/payments" },
  ]);

  const onDocumentLoadSuccess = ({ numPages }) => setNumPages(numPages);

  return (
    <>
      <EntityTable
        ref={ref}
        endpoint={`${API_BASE_URL}/api/v1/payments`}
        columns={columns}
        // today's rows get a light tint, to spot them at a glance (same as the sales listing)
        rowClassName={(row) => (isToday(row.paymentCreatedAt) ? 'row-today' : '')}
        defaultSort="id"
        defaultDirection="desc"
        pageSize={20}
        emptyMessage={t('payments.empty')}
        searchable={true}
        searchPlaceholder={t('payments.search_placeholder')}
        multiSelect={true}
        enableCardView={true}
        renderCard={renderPaymentCard}
      />

      <Modal
        title={t('payments.receipt_modal_title')}
        show={showPdfModal}
        onClose={() => { setShowPdfModal(false); setPdfData(null); }}
        classSize="modal-lg"
      >
        {pdfData ? (
          <>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <Document file={pdfData} onLoadSuccess={onDocumentLoadSuccess}>
                <Page pageNumber={pageNumber} width={680} />
              </Document>
            </div>

            {numPages > 1 && (
              <div className="d-flex justify-content-between align-items-center mt-3">
                <button className="btn btn-outline-primary btn-sm"
                  disabled={pageNumber <= 1} onClick={() => setPageNumber(pageNumber - 1)}>
                  {t('Previous')}
                </button>
                <span className="text-muted small">{t('pdf_viewer.page_indicator', { page: pageNumber, total: numPages })}</span>
                <button className="btn btn-outline-primary btn-sm"
                  disabled={pageNumber >= numPages} onClick={() => setPageNumber(pageNumber + 1)}>
                  {t('Next')}
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-4 text-muted">{t('payments.receipt_loading')}</div>
        )}
      </Modal>
    </>
  );
}
